import { createAsyncThunk } from "@reduxjs/toolkit";
import { showAlert } from "../slices/features/alert";
import { getBaseUrl } from "../selectors/features";
import { UserProfileService } from "@root/services/user-profile-change";
import {
  UPLOAD_FILE,
  USER_PROFILE,
  UPDATE_TOUR_COMPLETION,
} from "../action-types/userProfile";
import { TOAST_VARIANT } from "@root/constants/common";
import { t } from "i18next";
import { getLoggedInUser } from "../selectors/entities";

const userProfileService = new UserProfileService();

interface UserProfilePayload {
  payload: any;
  fullname: any;
  introduction: any;
  selectedFile: any;
  onSuccess: any;
  onError: any;
  disableAlert?: boolean;
}

export const userProfile = createAsyncThunk<TObject, TObject, IActionOptions>(
  USER_PROFILE,
  async (requestPayload: UserProfilePayload, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const userProfile: any = getLoggedInUser(thunkAPI.getState());
    let updatedUser = {};
    const { payload, disableAlert, onError, onSuccess } = requestPayload;
    const {
      fullname,
      bio,
      selectedFile,
      ai_data_retention,
      default_theme,
      preferred_language,
      avatar,
    } = payload;
    const formData: allAnyTypes = new FormData();
    if (ai_data_retention || ai_data_retention === false) {
      formData.append("ai_data_retention", ai_data_retention);
    }
    if (default_theme) {
      formData.append("default_theme", default_theme);
    }
    if (preferred_language) {
      formData.append("preferred_language", preferred_language);
    }
    if (bio) {
      formData.append("bio", bio);
    }
    if (fullname) {
      formData.append("fullname", fullname);
    }
    if (selectedFile) {
      formData.append("file", selectedFile);
    }
    if (avatar || avatar === "") {
      formData.append("avatar", avatar);
    }

    const { data, error } = await userProfileService.userProfile(
      baseUrl,
      formData
    );

    if (error) {
      onError();
      thunkAPI.dispatch(
        showAlert({
          message:
            data?.message || data?.userMessage || data?.code || data || "",
          type: TOAST_VARIANT.ERROR,
        })
      );
      return thunkAPI.rejectWithValue(data);
    } else {
      onSuccess();
      if (!disableAlert) {
        thunkAPI.dispatch(
          showAlert({
            message: t("common:profile.profileUpdate"),
            type: TOAST_VARIANT.SUCCESS,
          })
        );
      }
      // The API response contains the updated user object in data.data
      // Merge it with existing user data to preserve all fields
      const updatedUserData = data?.data || data;

      // If file was uploaded, data might be just the avatar URL string
      // Otherwise, it should be the complete user object
      if (selectedFile && typeof updatedUserData === "string") {
        // Only avatar URL returned, merge with existing user data
        updatedUser = {
          ...userProfile,
          avatar: updatedUserData,
        };
      } else {
        // Complete user object returned, merge with existing data
        updatedUser = {
          ...userProfile,
          ...updatedUserData,
        };
      }
    }

    return thunkAPI.fulfillWithValue(updatedUser);
  }
);

export const uploadFile = createAsyncThunk<TObject, TObject, IActionOptions>(
  UPLOAD_FILE,
  async (requestPayload: any, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { onError, onSuccess, payload, pid } = requestPayload;
    const { data, error } = await userProfileService.uploadFile(
      baseUrl,
      payload,
      pid
    );

    if (error) {
      onError(error, UPLOAD_FILE, data);
      return thunkAPI.rejectWithValue(error);
    } else {
      onSuccess(data, UPLOAD_FILE);
    }
    return thunkAPI.fulfillWithValue(data);
  }
);

interface UpdateTourCompletionPayload {
  payload: TObject; // e.g., { legal_standard_tour_completed: true }
  onSuccess?: () => void;
  onError?: () => void;
}

export const updateTourCompletion = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(
  UPDATE_TOUR_COMPLETION,
  async (requestPayload: UpdateTourCompletionPayload, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const userProfile: any = getLoggedInUser(thunkAPI.getState());
    const { payload, onError, onSuccess } = requestPayload;

    const { data, error } = await userProfileService.updateTourCompletion(
      baseUrl,
      payload
    );

    if (error) {
      onError?.();
      return thunkAPI.rejectWithValue(data);
    } else {
      onSuccess?.();
      // Update Redux state based on the payload we sent, not the API response
      // This ensures the state is updated immediately with the values we know we're setting
      const updatedUser = {
        ...userProfile,
        ...payload,
      };
      return thunkAPI.fulfillWithValue(updatedUser);
    }
  }
);
